import React, { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Components
import CompanyA from "./company-a.js";
import CompanyB from "./company-b.js";
import Lampiran from "./Lampiran.js";
import DaftarPersonalCommission from "./dpc.js";
import ListActiveUser from "./list-active-user.js";
import Button from "components/v1/Button.js";
import ModalKonfirmasiPembayaran from "./modal-konfirmasi-pembayaran";

// Assets
import IArrowBack from "assets/i-arrow-back.js";
import IDownload from "assets/i-download-2.js";
import ICheckRounded from "assets/i-check-rounded.js";

// Hooks
import { useGetDetailInvoiceV2 } from "api/invoice/query";
import {
  usePostDownloadInvoice,
  usePostPaymentInvoice,
} from "api/invoice/mutation";

// Helpers
import handleError from "utils/handleError.js";

const roundAmount = (amount) => {
  if (!amount) return "";

  let temp = amount;

  if (typeof amount !== "number") temp = parseInt(amount);

  return Math.round(temp).toLocaleString("id-ID");
};

const Detail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const year = searchParams.get("year");
  const month = searchParams.get("month");
  const companyId = searchParams.get("company_id");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: dataInvoice } = useGetDetailInvoiceV2({
    params: { year, month, company_id: companyId },
    enabled: !!params.id,
  });
  const fmtData = dataInvoice?.data?.data;

  const { mutate: postDownloadInvoice } = usePostDownloadInvoice();
  const { mutate: postPaymentInvoice } = usePostPaymentInvoice();

  const handleDownloadInvoice = () => {
    postDownloadInvoice(
      { params: { year, month, company_id: companyId } },
      {
        onSuccess: (res) => {
          try {
            let blob;

            if (res.data instanceof ArrayBuffer) {
              // If it's ArrayBuffer, use directly
              blob = new Blob([res.data], { type: "application/pdf" });
            } else if (res.data instanceof Blob) {
              // If it's already a Blob, use directly
              blob = res.data;
            } else {
              // Fallback: handle as before
              blob = new Blob([res.data], { type: "application/pdf" });
            }

            // Create download URL
            const url = window.URL.createObjectURL(blob);

            // Create temporary link element
            const link = document.createElement("a");
            link.href = url;
            link.download = `invoice-${companyId}-${year}-${month}.pdf`;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("Invoice downloaded successfully");
          } catch (error) {
            console.error("Download error:", error);
            toast.error("Failed to download invoice");
          }
        },
        onError: (error) => {
          toast.error(handleError(error));
        },
      },
    );
  };

  const handleBayarSekarang = () => {
    setIsModalOpen(true);
  };

  const handleConfirmPayment = () => {
    postPaymentInvoice(
      { params: { year, month } },
      {
        onSuccess: (response) => {
          setIsModalOpen(false);
          toast.success("Pembayaran sedang diproses");
          const url =
            response?.data?.data?.payment_url ?? "";

          if (url) {
            window.open(url, "_self");
          }
        },
        onError: (error) => {
          setIsModalOpen(false);
          toast.error(handleError(error));
        },
      },
    );
  };

  return (
    <div className="flex flex-col justify-between">
      <div className="bg-[#E6E7E8] w-full h-[calc(100vh-68px)] overflow-y-auto">
        {/* Invoice */}
        <div className="px-6 py-[12px] bg-white rounded-[4px]">
          {/* Header */}
          <div className="py-2 mb-2 bg-white flex items-center justify-between border-b border-solid border-[#D8D8D9]">
            <button
              onClick={() => navigate("/invoice")}
              className="rounded-[6px] gap-2 border border-solid border-[#D8D8D9] px-[12px] h-[32px] flex items-center justify-center"
            >
              <IArrowBack />
              Kembali
            </button>
            <button
              onClick={handleDownloadInvoice}
              className="rounded-[6px] gap-2 border border-solid border-[#D8D8D9] px-[12px] h-[32px] flex items-center justify-center"
            >
              <IDownload />
              Download Invoice
            </button>
          </div>

          {/* Body */}
          <div className="mt-2 rounded-[4px]">
            <CompanyA data={fmtData} />
            <CompanyB data={fmtData} />
          </div>
        </div>

        {/* Lampiran */}
        <div className="mt-[12px] py-[12px] bg-white rounded-[4px]">
          <Lampiran data={fmtData} />
          <DaftarPersonalCommission data={fmtData} />
          <ListActiveUser data={fmtData} />
        </div>
      </div>

      {/* Total */}
      <div className="bg-white border-t border-solid border-red mt-2 pt-2 px-6 flex items-center justify-between">
        <div className={fmtData?.management_fee_invoice?.status === "paid" ? "flex items-center justify-between w-full" : ""}>
          <p className="text-[11px] leading-[17.798px] tracking-[-0.4px] text-[#252526]">
            Total yang harus dibayar untuk{" "}
            <span className="text-[#FD4949]">*</span>
            <span className="font-semibold">
              Jasa Manajemen dan Biaya Subscription
            </span>
          </p>
          <p className="text-[13px] font-semibold leading-[21.034px] tracking-[-0.4px] text-[#252526] flex items-center gap-1">
            {fmtData?.management_fee_invoice?.status === "paid" && <ICheckRounded width="16" height="16" color="#0D814A" />} Rp{roundAmount(fmtData?.combined_total)}
          </p>
        </div>
       {fmtData?.management_fee_invoice?.status === "pending" && <Button
          type="primary"
          text="Bayar Sekarang"
          onClick={handleBayarSekarang}
        />}
      </div>

      <ModalKonfirmasiPembayaran
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmPayment}
      />
    </div>
  );
};

export default Detail;
